import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signIn, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { amplifyConfig } from '../amplify-config';
import axios from 'axios';

// Configure Amplify
Amplify.configure(amplifyConfig);

interface AuthContextType {
  user: any | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<any | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    checkUser();
  }, []);
  
  const checkUser = async () => {
    try {
      console.log('Checking current user...');
      const currentUser = await getCurrentUser();
      console.log('Current user:', currentUser);
      setUser(currentUser);
      
      const session = await fetchAuthSession();
      console.log('Session:', session);
      const idToken = session.tokens?.idToken?.toString();
      console.log('Token received:', idToken ? 'Yes' : 'No');
      setToken(idToken || null);
      
      // Configure axios for API calls
      axios.defaults.baseURL = 'https://api.finapp.aerotage.com';
      if (idToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
      } else {
        delete axios.defaults.headers.common['Authorization'];
      }
      
      // Enforce HTTPS and absolute API URL at runtime (defensive)
      axios.interceptors.request.use((config) => {
        const apiHost = 'api.finapp.aerotage.com';
        if (config.url) {
          // If relative path, prefix with HTTPS API origin
          if (config.url.startsWith('/')) {
            config.url = `https://${apiHost}${config.url}`;
          }
          // If any accidental http://api... slipped in, upgrade to https
          if (config.url.startsWith('http://'+apiHost)) {
            config.url = config.url.replace('http://', 'https://');
          }
        }
        return config;
      });
    } catch (error) {
      console.error('Check user error:', error);
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  };
  
  const login = async (email: string, password: string) => {
    try {
      console.log('SignIn attempt...', email);
      const result = await signIn({ username: email, password });
      console.log('SignIn result:', result);
      
      // Force immediate user check
      await checkUser();
      
      // If we get here, login was successful
      console.log('Login complete');
    } catch (error) {
      console.error('SignIn error:', error);
      throw error;
    }
  };
  
  const logout = async () => {
    await signOut();
    setUser(null);
    setToken(null);
    // Clear axios auth header
    delete axios.defaults.headers.common['Authorization'];
  };
  
  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

